import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import Modal from 'ui/Modal';
import { Drawer, DrawerContent } from 'ui/Drawer';
import api from 'config/axiosConfig';
import EntityAvatar from 'components/Entities/EntityAvatar';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { getActorDetails } from 'util/getObjectDetails';
import { MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'ui/DropdownMenu';
import { toast } from 'sonner';
import { apiRoutes } from "util/apiRoutes";

interface TeamMember {
    id: number;
    member_id: number;
    member_type: 'user' | 'team';
    team_id: number;
    properties: {
        role: 'founder' | 'moderator' | 'member';
        is_active: boolean;
        created_at: string;
        set_at: string;
    };
}

interface Props {
    teamId: number;
    teamName: string;
    isTeamModerator: boolean;
    onClose: () => void;
}

const TeamMembersModal: React.FC<Props> = ({ teamId, teamName, isTeamModerator, onClose }) => {
    const { userIsMobile } = useUser();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [enrichedMembers, setEnrichedMembers] = useState<any[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get(apiRoutes.team.members(teamId));

                // Enrich the members data
                const enriched = await Promise.all(response.data.map(async (member: TeamMember) => {
                    const enrichedData = await getActorDetails(member.member_type, member.member_id);
                    
                    return {
                        ...member,
                        id: member.member_id,
                        type: member.member_type,
                        member_entity: enrichedData,
                        properties: {
                            role:(member as any).properties?.is_creator ? 'founder' : member.properties.role
                        }
                    };
                }));

                // Sort members by role: creator > moderator > member
                const sortedMembers = enriched.sort((a, b) => {
                    const roleOrder: Record<string, number> = { founder: 0, moderator: 1, member: 2 };
                    return roleOrder[a.properties.role] - roleOrder[b.properties.role];
                });

                setEnrichedMembers(sortedMembers);
            } catch (error) {
                console.error('Error fetching team members:', error);
                setError('Failed to load team members.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [teamId]);

    const handleRemoveMember = async (memberId: number, memberType: string) => {
        try {
            await api.delete(apiRoutes.team.members(teamId), {
                data: {
                    memberId,
                    memberType
                }
            });

            // Remove member from state
            setEnrichedMembers(prev => prev.filter(member => 
                !(member.member_id === memberId && member.member_type === memberType)
            ));

            toast.success('Member removed successfully');
        } catch (error) {
            console.error('Error removing member:', error);
            toast.error('Failed to remove member');
        }
    };

    const handlePromoteToModerator = async (memberId: number, memberType: string) => {
        try {
            await api.patch(apiRoutes.team.memberRole(teamId), {
                memberId,
                memberType,
                role: 'moderator'
            });

            // Update member role in state
            setEnrichedMembers(prev => prev.map(member => {
                if (member.member_id === memberId && member.member_type === memberType) {
                    return {
                        ...member,
                        properties: {
                            ...member.properties,
                            role: 'moderator'
                        }
                    };
                }
                return member;
            }));

            toast.success('Member promoted to moderator');
        } catch (error) {
            console.error('Error promoting member:', error);
            toast.error('Failed to promote member');
        }
    };

    const MemberActions = ({ member }: { member: any }) => {
        if (!isTeamModerator || member.properties.role === 'founder' || member.properties.is_creator) return null;

        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="p-1 hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover rounded-full">
                        <MoreHorizontal className="size-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {member.properties.role !== 'moderator' && (
                        <DropdownMenuItem onClick={() => handlePromoteToModerator(member.member_id, member.member_type)}>
                            Promote to moderator
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                        onClick={() => handleRemoveMember(member.member_id, member.member_type)}
                        className="text-warning-red dark:text-warning-red-dark"
                    >
                        Remove from team
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    };

    const getRoleBadge = (role: string) => {
        const badges = {
            founder: "bg-purple-500/10 text-purple-500 dark:bg-purple-400/10 dark:text-purple-400",
            moderator: "bg-blue-500/10 text-blue-500 dark:bg-blue-400/10 dark:text-blue-400",
            member: "bg-gray-500/10 text-gray-500 dark:bg-gray-400/10 dark:text-gray-400",
            requested: "bg-yellow-500/10 text-yellow-500 dark:bg-yellow-400/10 dark:text-yellow-400",
            invited: "bg-pink-500/10 text-pink-500 dark:bg-pink-400/10 dark:text-pink-400"
        };
        
        return (
            <span className={`text-xs px-2 py-0.5 rounded-full ${badges[role as keyof typeof badges]}`}>
                {role}
            </span>
        );
    };

    const content = (
        <div className="flex flex-col space-y-4">
            {loading ? (
                <LoadingSkeleton variant="entity-avatar" rounded="rounded-md" padding="p-2" margin="mx-2" avatarSize="h-9 w-9" count={10} />
            ) : error ? (
                <div className="text-warning-red dark:text-warning-red-dark text-center p-4">
                    {error}
                </div>
            ) : enrichedMembers.length === 0 ? (
                <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-center p-4">
                    No members yet
                </div>
            ) : (
                <div className="space-y-2">
                    {enrichedMembers.map((member) => (
                        <div key={`${member.type}-${member.id}`} className="flex items-center justify-between p-2">
                            <div className="flex items-center gap-3 flex-grow">
                                <EntityAvatar
                                    entity={member.member_entity}
                                    entityType={member.type}
                                    entityChip={true}
                                    showCloseChip={false}
                                    isInputField={false}
                                    showLink={true}
                                    badgeElement={getRoleBadge(member.properties.role)}
                                />
                            </div>
                            <MemberActions member={member} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return userIsMobile ? (
        <Drawer open={true} onOpenChange={onClose}>
            <DrawerContent title={`${teamName} members`}>
                {content}
            </DrawerContent>
        </Drawer>
    ) : (
        <Modal 
            isOpen={true} 
            onClose={onClose} 
            title={`${teamName} members`}
        >
            {content}
        </Modal>
    );
};

export default TeamMembersModal; 